// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Offline Status Indicator Component
 * Shows when the user is offline and sync status
 */

import { useState, useEffect, useCallback } from 'react';
import { logger } from '../lib/logger';
import { offlineStorage } from '../lib/offline';
import { backgroundSync } from '../lib/sync';

interface SyncStatus {
  isOnline: boolean;
  pendingActions: number;
  lastSync: Date | null;
  isSyncing: boolean;
}

export function OfflineIndicator() {
  const [status, setStatus] = useState<SyncStatus>({
    isOnline: navigator.onLine,
    pendingActions: 0,
    lastSync: null,
    isSyncing: false,
  });
  const [showDetails, setShowDetails] = useState(false);
  const [animateSync, setAnimateSync] = useState(false);

  // Define updatePendingCount as useCallback to avoid stale closure issues
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await backgroundSync.getPendingActionCount();
      setStatus(prev => ({ ...prev, pendingActions: count }));
    } catch (error) {
      logger.error('Failed to get pending action count', { error });
    }
  }, []);

  useEffect(() => {
    // Subscribe to online status changes
    const unsubscribeOnline = backgroundSync.onOnlineStatusChange(online => {
      setStatus(prev => ({ ...prev, isOnline: online }));
    });

    // Subscribe to sync completions
    const unsubscribeSync = backgroundSync.onSyncComplete(synced => {
      setStatus(prev => ({
        ...prev,
        lastSync: new Date(),
        isSyncing: false,
      }));

      if (synced) {
        setAnimateSync(true);
        setTimeout(() => setAnimateSync(false), 2000);
      }

      // Update pending count
      updatePendingCount();
    });

    // Initial pending count - use queueMicrotask to avoid setState in effect body
    queueMicrotask(() => {
      void updatePendingCount();
    });

    // Periodic check for pending actions
    const intervalId = setInterval(() => updatePendingCount(), 30000);

    return () => {
      unsubscribeOnline();
      unsubscribeSync();
      clearInterval(intervalId);
    };
  }, [updatePendingCount]);

  const handleSync = async () => {
    if (!status.isOnline || status.isSyncing) return;

    setStatus(prev => ({ ...prev, isSyncing: true }));
    logger.info('Manual sync triggered');

    try {
      await backgroundSync.syncQueuedActions();
    } catch (error) {
      logger.error('Manual sync failed', { error });
      setStatus(prev => ({ ...prev, isSyncing: false }));
    }
  };

  const handleClearStorage = async () => {
    if (!confirm('Clear all offline data? This cannot be undone.')) return;

    try {
      await offlineStorage.clearAll();
      setStatus(prev => ({ ...prev, pendingActions: 0 }));
      logger.info('Offline storage cleared by user');
    } catch (error) {
      logger.error('Failed to clear offline storage', { error });
    }
  };

  // If online and no pending actions, show minimal indicator
  if (status.isOnline && status.pendingActions === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-40" data-testid="offline-indicator">
      {/* Main indicator */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        data-testid="offline-indicator-button"
        className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg transition-all ${
          status.isOnline
            ? status.pendingActions > 0
              ? 'bg-yellow-500 text-yellow-900'
              : 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        } ${animateSync ? 'animate-pulse' : ''}`}
      >
        <span
          className={`w-3 h-3 rounded-full ${status.isOnline ? 'bg-white' : 'bg-red-300 animate-pulse'}`}
        />
        <span className="text-sm font-medium">
          {!status.isOnline
            ? 'Offline'
            : status.pendingActions > 0
              ? `${status.pendingActions} pending`
              : 'Online'}
        </span>
        {status.isSyncing && <span className="animate-spin">&#8635;</span>}
      </button>

      {/* Expanded details panel */}
      {showDetails && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Connection Status</h4>

          <div className="space-y-3">
            {/* Online status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Network</span>
              <span
                className={`text-sm font-medium ${status.isOnline ? 'text-green-600' : 'text-red-600'}`}
              >
                {status.isOnline ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* Pending actions */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Pending Actions</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {status.pendingActions}
              </span>
            </div>

            {/* Last sync */}
            {status.lastSync && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Last Sync</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {status.lastSync.toLocaleTimeString()}
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <button
                onClick={handleSync}
                disabled={!status.isOnline || status.isSyncing || status.pendingActions === 0}
                className="w-full px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {status.isSyncing ? 'Syncing...' : 'Sync Now'}
              </button>

              <button
                onClick={handleClearStorage}
                className="w-full px-3 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Clear Offline Data
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={() => setShowDetails(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

export default OfflineIndicator;
